import type { ReportComponent } from '@/types/reports';

export type SectionAnalytics = {
  sectionName: string;
  linkEvent?: string;
  specialName?: string;
  requiresSpecialName?: boolean;
};

const SPECIAL_NAME_REGEX = /^[a-z0-9_]{1,60}$/;

export const toSpecialNameSlug = (value?: string | null): string | undefined => {
  if (!value?.trim()) return undefined;
  const slug = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

  if (!slug || !SPECIAL_NAME_REGEX.test(slug)) return undefined;
  return slug;
};

export const resolveNewsSectionNameFromBadge = (badgeLabel?: string | null): string => {
  const badge = String(badgeLabel ?? '').trim().toLowerCase();
  if (badge === 'formato') return 'formatos';
  if (badge === 'assunto') return 'assuntos';
  if (badge === 'trends') return 'trends';
  if (badge === 'influenciador') return 'trends';
  return 'assuntos';
};

const resolveNewsSectionName = (component: ReportComponent): string =>
  resolveNewsSectionNameFromBadge(String(component.props?.badgeLabel ?? ''));

export const resolveSectionAnalytics = (
  component: ReportComponent
): SectionAnalytics => {
  const titleSlug = toSpecialNameSlug(
    String(component.props?.title ?? component.props?.sectionTitle ?? '')
  );

  switch (component.type) {
    case 'hero':
      return {
        sectionName: 'hero',
      };
    case 'highlights-card':
      return {
        sectionName: 'highlights',
        linkEvent: 'high_hor_link_clicked',
      };
    case 'today-carousel':
      return {
        sectionName: 'highlights',
        linkEvent: 'high_hor_link_clicked',
      };
    case 'tiktok-trends':
      return {
        sectionName: 'trends',
        linkEvent: 'trends_link_clicked',
        specialName: titleSlug ?? toSpecialNameSlug('tiktok_trends'),
        requiresSpecialName: true,
      };
    case 'news-card': {
      const sectionName = resolveNewsSectionName(component);
      const linkEvent =
        sectionName === 'formatos'
          ? 'formatos_link_clicked'
          : sectionName === 'trends'
            ? 'trends_link_clicked'
            : 'assuntos_link_clicked';
      return {
        sectionName,
        linkEvent,
        specialName: titleSlug,
        requiresSpecialName: true,
      };
    }
    case 'business-card':
      return {
        sectionName: 'business',
        linkEvent: 'business_link_clicked',
        specialName: titleSlug,
        requiresSpecialName: true,
      };
    case 'thematic-session':
      return {
        sectionName: 'especial_content',
        linkEvent: 'especial_content_link_clicked',
        specialName: titleSlug,
      };
    case 'curatorship':
      return {
        sectionName: 'curadoria',
        linkEvent: 'curadoria_link_clicked',
        specialName: titleSlug,
        requiresSpecialName: true,
      };
    case 'collapsible-hashtags':
    case 'collapsible-creators':
    case 'collapsible-brands':
      return {
        sectionName: 'trends',
        linkEvent: 'trends_link_clicked',
        specialName: titleSlug ?? toSpecialNameSlug(component.type),
        requiresSpecialName: true,
      };
    default:
      return {
        sectionName: component.type,
      };
  }
};

export const FOOTER_SECTION_ANALYTICS: SectionAnalytics = {
  sectionName: 'footer',
  linkEvent: 'footer_link_clicked',
};
